import type { Accessor, ParentComponent, Setter } from "solid-js";
import { createContext, createMemo, createSignal } from "solid-js";
import { initializedContext } from "../../../utils/initializedUtil";
import type { ColumnDefWithId } from "./Table";
import { isEmpty } from "lodash-es";
import type { FilterStore } from "../../../utils/viewConfigurationStore";

type TableContextValue = {
  filterStore: Accessor<FilterStore | undefined>;
  setFilterStore: Setter<FilterStore | undefined>;
  rowSelection: Accessor<Record<string, boolean>>;
  columnVisibility: Accessor<string[]>;
  filterableColumns: Accessor<ColumnDefWithId<unknown>[]>;
  totalCount: Accessor<number>;
  isEmpty: Accessor<boolean>;
  isInitialized: Accessor<boolean>;
  setRowSelection: Setter<Record<string, boolean>>;
  setColumnVisibility: (ids: string[]) => void;
  setFilterableColumns: (columns: ColumnDefWithId<unknown>[]) => void;
  setTotalCount: (count: number) => void;
  triggerCreateFilter: (columnId: string) => void;
  setCreateFilterHandler: (fn: (columnId: string) => void) => void;
};

const TableContext = createContext<TableContextValue>();

export const TableProvider: ParentComponent = (props) => {
  const [filterStore, setFilterStore] = createSignal<FilterStore>();
  const [rowSelection, setRowSelection] = createSignal<Record<string, boolean>>({});
  const [columnVisibility, setColumnVisibility] = createSignal<string[]>([]);
  const [filterableColumns, setFilterableColumns] = createSignal<ColumnDefWithId<unknown>[]>([]);
  const [totalCount, setTotalCount] = createSignal(-1); // -1 = not yet initialized
  const [createFilterHandler, setCreateFilterHandlerSignal] = createSignal<(columnId: string) => void>(() => undefined);

  const isTableEmpty = createMemo(() => isEmpty(filterStore()?.all()) && totalCount() === 0);
  const isInitialized = createMemo(() => totalCount() >= 0);

  const value: TableContextValue = {
    filterStore,
    setFilterStore,
    rowSelection,
    columnVisibility,
    filterableColumns,
    totalCount,
    isEmpty: isTableEmpty,
    isInitialized,
    setRowSelection,
    setColumnVisibility,
    setFilterableColumns: (columns) => setFilterableColumns(columns),
    setTotalCount,
    triggerCreateFilter: (columnId) => createFilterHandler()(columnId),
    setCreateFilterHandler: (fn) => setCreateFilterHandlerSignal(() => fn),
  };

  return <TableContext.Provider value={value}>{props.children}</TableContext.Provider>;
};

export const useTable = () => initializedContext(TableContext, "useTableContext", "TableProvider");
